<template>
  <aside
    class="route-panel"
    :class="{ 'collapsed': isCollapsed, 'condensed': routes.length <= 1 }"
    :aria-labelledby="headingId"
    role="complementary"
  >
    <!-- header --------------------------------------------------------->
    <header class="panel-header">
      <h2
        :id="headingId"
        class="panel-title"
        :contenteditable="!isEditing"
        @click="startEditing"
        @blur="saveTitle"
        @keydown.enter.prevent="handleEnterKey"
        ref="titleElement"
      >{{ displayTitle }}</h2>

      <!-- "hamburger" collapse icon -->
      <button
        class="icon-btn"
        aria-label="Collapse routes panel"
        @click="toggleCollapse"
      >
        <span class="material-icons">left_panel_close</span>
      </button>
    </header>

    <!-- action buttons ------------------------------------------------->
    <section class="panel-actions" style="margin-bottom: 1rem !important;">
      <button class="panel-action-btn" @click="handleImport($event)">
        <span class="material-icons mr-1">upload</span>
        Import Hike
      </button>
      <button class="panel-action-btn" @click="handleAddNew($event)">
        <span class="material-icons mr-1">add</span>
        Add Route
      </button>
    </section>

    <!-- route list ----------------------------------------------------->
    <div
      v-if="routes.length > 1"
      class="route-list-container"
    >
      <!-- Single route without days -->
      <draggable
        v-if="!shouldShowDays"
        v-model="mutableRoutes"
        :item-key="item => item.id"
        tag="ul"
        class="route-stack"
        ghost-class="route-card--ghost"
        handle=".route-drag-handle"
        @start="onRouteDragStart"
        @end="onDragEnd"
        :move="onRouteMove"
      >
        <template #item="{ element: route, index: idx }">
          <li
            class="route-item route-card"
            :class="{ 'route-card--active': route.id === activeRouteId }"
            @click="selectRoute(route)"
          >
            <!-- drag handle -->
            <span class="route-drag-handle">
              <span class="material-icons"></span>
            </span>

            <!-- primary data -->
            <div class="route-info">
            <!-- route style label -->
              <span class="route-style-label" :class="`route-style-label--${route.route_style || 'route'}`">
                {{ (route.route_style || 'route').toUpperCase() }}
              </span>
              <!-- Reference badge -->
              <span v-if="route.isReference" class="route-reference-badge" title="Referenced Route - Changes affect all hikes using this route">
                <span class="material-icons">link</span>
              </span>
              <span
                class="route-name"
                :contenteditable="editingRouteId === route.id"
                :data-route-id="route.id"
                @click.stop="startEditingRouteName(route)"
                @blur="saveRouteName(route)"
                @keydown.enter.prevent="handleRouteEnterKey(route)"
                @keydown.esc="cancelRouteEdit(route)"
              >{{ route.name }}</span>

              <small class="route-meta">
                {{ formatDistance(route.distance) }}
                <template v-if="route.route_style !== 'transport'">
                  • {{ formatDistance(route.ascent, false) }}
                  <template v-if="route.duration"> • {{ route.duration }}</template>
                </template>
              </small>
            </div>

            <!-- overflow menu -->
            <div class="route-card__actions">
              <button
                class="icon-btn"
                aria-haspopup="true"
                aria-label="Route options"
                @click.stop="openMenu(idx, $event)"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                  <circle cx="12" cy="5" r="2" />
                  <circle cx="12" cy="12" r="2" />
                  <circle cx="12" cy="19" r="2" />
                </svg>
              </button>

              <ul
                v-show="menuOpen === idx"
                class="menu-dropdown"
                :style="{ top: menuPosition.top, left: menuPosition.left, transform: menuPosition.transform }"
                @mouseleave="menuOpen = null"
              >
                <li><button @click="handleEdit(route)">Edit Name</button></li>
                <li><button @click="handleSetRouteStyle(route)">Set route style</button></li>
                <li><button @click="handleReplace(route)">Replace with GPX</button></li>
                <li v-if="route.route_style !== 'transport'"><button @click="launchFlyoverForRoute(route)">3D Preview</button></li>
                <li><button @click="mergeWithPrevious(route)" :disabled="idx === 0">Merge with previous</button></li>
                <li><button @click="mergeWithNext(route)" :disabled="idx === routes.length - 1">Merge with next</button></li>
                <li v-if="route.isReference"><button @click="showUsageModal(route)">See usage</button></li>
                <li><button @click="handleDelete(route)" :disabled="routes.length <= 1">Delete</button></li>
              </ul>
            </div>
          </li>
        </template>
      </draggable>

      <!-- Multiple routes with days -->
      <div v-else class="days-wrapper">
        <draggable
          v-model="dayOrder"
          :item-key="item => item.dayNumber"
          tag="div"
          class="days-container"
          ghost-class="day-card--ghost"
          handle=".day-drag-handle"
          :group="{ name: 'days', pull: false, put: false }"
          @start="onDayDragStart"
          @end="onDayDragEnd"
          :move="onDayMove"
        >
          <template #item="{ element: dayItem }">
            <div
              :key="`day-${dayItem.dayNumber}`"
              class="day-card"
              :class="{
                'day-card--inactive': dayItem.isPlaceholder,
                'day-card--dragging': isDayDragging && !dayItem.isPlaceholder
              }"
            >
              <div class="day-card__header">
                <div class="day-card__title">
                  <button
                    v-if="!dayItem.isPlaceholder"
                    class="icon-btn day-drag-handle"
                    title="Drag day"
                    aria-label="Drag day"
                  >
                    <span class="material-icons">drag_indicator</span>
                  </button>
                  <div class="day-card__title-text">
                    <span
                      class="day-card__name"
                      :contenteditable="editingDayNumber === dayItem.dayNumber"
                      :data-day-number="dayItem.dayNumber"
                      @click.stop="startEditingDayName(dayItem)"
                      @blur="saveDayName(dayItem)"
                      @keydown.enter.prevent="handleDayEnterKey(dayItem)"
                      @keydown.esc="cancelDayEdit(dayItem)"
                    >{{ dayNames[dayItem.dayNumber] || `Day ${dayItem.dayNumber}` }}</span>
                  </div>
                </div>
                <div class="day-card__actions">
                  <div class="day-menu">
                    <button
                      class="icon-btn"
                      aria-haspopup="true"
                      aria-label="Day options"
                      @click="openDayMenu(dayItem.dayNumber, $event)"
                    >
                      <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                        <circle cx="12" cy="5" r="2" />
                        <circle cx="12" cy="12" r="2" />
                        <circle cx="12" cy="19" r="2" />
                      </svg>
                    </button>
                    <ul
                      v-show="dayMenuOpen === dayItem.dayNumber"
                      class="menu-dropdown"
                :style="{ top: menuPosition.top, left: menuPosition.left, transform: menuPosition.transform }"
                      @mouseleave="dayMenuOpen = null"
                    >
                      <li><button @click="handleDayEdit(dayItem.dayNumber)">Edit day details</button></li>
                      <li><button @click="addRouteToDay(dayItem.dayNumber)">Add route to this day</button></li>
                      <li><button @click="deleteDay(dayItem.dayNumber)" :disabled="!canDeleteDay(dayItem.dayNumber)">Delete day</button></li>
                    </ul>
                  </div>
                </div>
              </div>
              <p v-if="dayDescriptions[dayItem.dayNumber]" class="day-card__description">{{ dayDescriptions[dayItem.dayNumber] }}</p>
              <draggable
                v-if="dayRoutes[dayItem.dayNumber] && dayRoutes[dayItem.dayNumber].length > 0"
                :list="dayRoutes[dayItem.dayNumber]"
                :item-key="item => item.id"
                tag="ul"
                class="route-stack"
                ghost-class="route-card--ghost"
                handle=".route-drag-handle"
                :group="{ name: 'routes', pull: true, put: true }"
                @start="onRouteDragStart"
                @end="onRouteDragEnd"
                @change="(evt) => onDragChange(evt, dayItem.dayNumber)"
                :data-day="dayItem.dayNumber"
                :move="onRouteMove"
              >
                <template #item="{ element: route }">
                  <li
                    class="route-item route-card"
                    :class="{ 'route-card--active': route.id === activeRouteId }"
                    @click="selectRoute(route)"
                    :data-day="dayItem.dayNumber"
                  >
                    <!-- drag handle -->
                    <span class="route-drag-handle">
                      <span class="material-icons">drag_indicator</span>
                    </span>

                    <!-- primary data -->
                    <div class="route-info">
                      <div class="d-flex gap-2 align-items-center">
                        <!-- Reference badge -->
                        <span v-if="route.isReference" class="route-reference-badge rounded-pill" title="Referenced Route - Changes affect all hikes using this route">
                          <span class="material-icons">link</span>
                        </span>
                        <!-- route style label -->
                        <span class="route-style-label" :class="`route-style-label--${route.route_style || 'route'}`">
                          {{ (route.route_style || 'route').toUpperCase() }}
                        </span>
                      </div>
                      <span
                        class="route-name"
                        :contenteditable="editingRouteId === route.id"
                        :data-route-id="route.id"
                        @click.stop="startEditingRouteName(route)"
                        @blur="saveRouteName(route)"
                        @keydown.enter.prevent="handleRouteEnterKey(route)"
                        @keydown.esc="cancelRouteEdit(route)"
                      >{{ route.name }}</span>

                      <small class="route-meta">
                        {{ formatDistance(route.distance) }}
                        <template v-if="route.route_style !== 'transport'">
                          • {{ formatDistance(route.ascent, false) }}
                          <template v-if="route.duration"> • {{ route.duration }}</template>
                        </template>
                      </small>
                    </div>

                    <!-- overflow menu -->
                    <div class="route-card__actions">
                      <button
                        class="icon-btn"
                        aria-haspopup="true"
                        aria-label="Route options"
                        @click.stop="openMenu(getRouteGlobalIndex(route), $event)"
                      >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                          <circle cx="12" cy="5" r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>

                      <ul
                        v-show="menuOpen === getRouteGlobalIndex(route)"
                        class="menu-dropdown"
                :style="{ top: menuPosition.top, left: menuPosition.left, transform: menuPosition.transform }"
                        @mouseleave="menuOpen = null"
                      >
                        <li><button @click="handleEdit(route)">Edit</button></li>
                        <li><button @click="handleSetRouteStyle(route)">Set route style</button></li>
                        <li><button @click="handleReplace(route)">Replace with GPX</button></li>
                        <li v-if="route.route_style !== 'transport'"><button @click="launchFlyoverForRoute(route)">3D Preview</button></li>
                        <li><button @click="mergeWithPrevious(route)" :disabled="isFirstRoute(route)">Merge with previous</button></li>
                        <li><button @click="mergeWithNext(route)" :disabled="isLastRoute(route)">Merge with next</button></li>
                        <li v-if="route.isReference"><button @click="showUsageModal(route)">See usage</button></li>
                        <li><button @click="handleDelete(route)" :disabled="routes.length <= 1">Delete</button></li>
                      </ul>
                    </div>
                  </li>
                </template>
              </draggable>
              <draggable
                v-else
                :list="getEmptyDayList(dayItem.dayNumber)"
                :item-key="item => item.id"
                tag="div"
                class="day-card__empty"
                ghost-class="route-card--ghost"
                handle=".route-drag-handle"
                :group="{ name: 'routes', pull: false, put: true }"
                @start="onRouteDragStart"
                @end="onRouteDragEnd"
                @change="(evt) => onDragChange(evt, dayItem.dayNumber)"
                :data-day="dayItem.dayNumber"
                :move="onRouteMove"
              >
                <template #header>
                  <span class="day-empty-text">(Add route to activate)</span>
                </template>
                <template #item="{ element: route }">
                  <li
                    class="route-item route-card"
                    :class="{ 'route-card--active': route.id === activeRouteId }"
                    @click="selectRoute(route)"
                    :data-day="dayItem.dayNumber"
                  >
                    <!-- drag handle -->
                    <span class="route-drag-handle">
                      <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
                        <path d="M9 6a1 1 0 11-2 0 1 1 0 012 0zm0 6a1 1 0 11-2 0 1 1 0 012 0zm0 6a1 1 0 11-2 0 1 1 0 012 0zm6-12a1 1 0 11-2 0 1 1 0 012 0zm0 6a1 1 0 11-2 0 1 1 0 012 0zm0 6a1 1 0 11-2 0 1 1 0 012 0z"/>
                      </svg>
                    </span>

                    <!-- route style label -->
                    <span class="route-style-label" :class="`route-style-label--${route.route_style || 'route'}`">
                      {{ (route.route_style || 'route').toUpperCase() }}
                    </span>

                    <!-- primary data -->
                    <div class="route-info">
                      <span
                        class="route-name"
                        :contenteditable="editingRouteId === route.id"
                        :data-route-id="route.id"
                        @click.stop="startEditingRouteName(route)"
                        @blur="saveRouteName(route)"
                        @keydown.enter.prevent="handleRouteEnterKey(route)"
                        @keydown.esc="cancelRouteEdit(route)"
                      >{{ route.name }}</span>
                      <br/>
                      <small class="route-meta">
                        {{ formatDistance(route.distance) }}
                        <template v-if="route.route_style !== 'transport'">
                          • {{ formatDistance(route.ascent, false) }}
                          <template v-if="route.duration"> • {{ route.duration }}</template>
                        </template>
                      </small>
                    </div>

                    <!-- overflow menu -->
                    <div class="route-card__actions">
                      <button
                        class="icon-btn"
                        aria-haspopup="true"
                        aria-label="Route options"
                        @click.stop="openMenu(getRouteGlobalIndex(route), $event)"
                      >
                        <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor">
                          <circle cx="12" cy="5" r="2" />
                          <circle cx="12" cy="12" r="2" />
                          <circle cx="12" cy="19" r="2" />
                        </svg>
                      </button>

                      <ul
                        v-show="menuOpen === getRouteGlobalIndex(route)"
                        class="menu-dropdown"
                :style="{ top: menuPosition.top, left: menuPosition.left, transform: menuPosition.transform }"
                        @mouseleave="menuOpen = null"
                      >
                        <li><button @click="handleEdit(route)">Edit</button></li>
                        <li><button @click="handleSetRouteStyle(route)">Set route style</button></li>
                        <li><button @click="handleReplace(route)">Replace with GPX</button></li>
                        <li v-if="route.route_style !== 'transport'"><button @click="launchFlyoverForRoute(route)">3D Preview</button></li>
                        <li><button @click="mergeWithPrevious(route)" :disabled="isFirstRoute(route)">Merge with previous</button></li>
                        <li><button @click="mergeWithNext(route)" :disabled="isLastRoute(route)">Merge with next</button></li>
                        <li v-if="route.isReference"><button @click="showUsageModal(route)">See usage</button></li>
                        <li><button @click="handleDelete(route)" :disabled="routes.length <= 1">Delete</button></li>
                      </ul>
                    </div>
                  </li>
                </template>
              </draggable>
            </div>
          </template>
        </draggable>
      </div>
    </div>

    <p v-else-if="routes.length === 0" class="no-routes">No routes yet – get planning! 🚀</p>

    <!-- footer totals -------------------------------------------------->
    <div class="panel-footer">
      <div class="footer-row">
        <span>Total{{ hasTransportRoutes ? ' (excl trspt)' : '' }}</span>
        <strong>
          {{ formatDistance(totalDistance) }} • {{ formatDistance(totalAscent, false) }}
          <template v-if="totalDurationSeconds > 0"> • {{ totalDuration }}</template>
        </strong>
      </div>
      <div class="footer-row">
        <span>Est. Difficulty</span>
        <button
          class="difficulty-button"
          :style="{ color: difficultyColor }"
          @click="showDifficultyDialog"
        >
          <strong class="difficulty-badge">
            <span class="material-icons text-muted" style="font-size: 14px">edit</span>
            {{ difficulty }}
            <span v-if="isDifficultyOverridden" class="override-indicator" title="Manually set">✎</span>
          </strong>
        </button>
      </div>
    </div>
  </aside>

  <!-- Expand button when collapsed -->
  <button
    v-if="isCollapsed"
    class="expand-button"
    @click="toggleCollapse"
    aria-label="Expand routes panel"
  >
    <span class="material-icons">left_panel_open</span>
    <span>Routes</span>
  </button>

  <!-- Route Style Dialog -->
  <RouteStyleDialog
    ref="routeStyleDialog"
    :selected="styleDialogRoute?.route_style || 'route'"
    @update:selected="updateRouteStyle"
    @close="styleDialogRoute = null"
  />

  <!-- Difficulty Dialog -->
  <PlanTrailDifficultyDialog
    ref="difficultyDialog"
    :currentDifficulty="store.state.planTrail.difficultyOverride"
    :calculatedDifficulty="calculatedDifficulty"
    @close=""
  />

  <!-- Day Edit Modal -->
  <DayEditModal
    :isOpen="dayEditModalOpen"
    :dayNumber="editingDay"
    :name="dayNames[editingDay] || ''"
    :description="dayDescriptions[editingDay] || ''"
    @close="dayEditModalOpen = false"
    @save="saveDayInfo"
  />

  <!-- Route Usage Modal -->
  <RouteUsageModal ref="routeUsageModal" />
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, watchEffect, reactive, nextTick } from "vue";
import { useStore } from "vuex";
import draggable from "vuedraggable";
import RouteStyleDialog from "./RouteStyleDialog.vue";
import PlanTrailDifficultyDialog from "./PlanTrailDifficultyDialog.vue";
import DayEditModal from "./DayEditModal.vue";
import RouteUsageModal from "./RouteUsageModal.vue";
import { Modal } from "bootstrap";
import {MeasurementPrefUtility} from "../../../utils/helpers/MeasurementPref";
import { plainToClass } from "class-transformer";
import { LineStringFeature } from "../../../utils/models/LineStringFeature";
import { OnBoardingGoal } from "../../../utils/models/OnBoardingGoal";
import { ensureFlyoverMounted } from "../services/FlyoverMountService";

// Get the Vuex store instance
const store = useStore();

// Computed properties from store
const routes = computed(() => store.getters['planTrail/formattedRoutesForComponent']);
const totalDistance = computed(() => store.getters['planTrail/totalDistanceExcludingTransport']);
const totalAscent = computed(() => store.getters['planTrail/totalAscent']);
const totalDuration = computed(() => store.getters['planTrail/totalDuration']);
const totalDurationSeconds = computed(() => store.getters['planTrail/totalDurationSeconds']);
const activeRouteId = computed(() => store.getters['planTrail/activeRouteId']);
const hasTransportRoutes = computed(() => store.getters['planTrail/hasTransportRoutes']);
const shouldShowDays = computed(() => store.getters['planTrail/shouldShowDays']);
const maxDay = computed(() => store.getters['planTrail/maxDay']);

// Get days to display — only days that have at least one route.
// Deleting the last route from a day removes it immediately.
const displayDays = computed(() => {
  if (!shouldShowDays.value) {
    return [];
  }

  return Object.keys(dayRoutes)
    .map(d => parseInt(d))
    .filter(d => dayRoutes[d]?.length > 0)
    .sort((a, b) => a - b);
});

// Get difficulty from store getters
const calculatedDifficulty = computed(() => store.getters['planTrail/calculatedDifficulty']);
const difficulty = computed(() => store.getters['planTrail/difficulty']);

// Check if difficulty is overridden
const isDifficultyOverridden = computed(() => {
  return store.state.planTrail.difficultyOverride !== null;
});

// Check if any route-style routes have coordinates for the 3D preview button
const hasRouteCoordinates = computed(() => {
  const routesArray = store.getters['planTrail/routesArray'];
  return routesArray.some(route =>
    route.route_style !== 'transport' &&
    route.lineStrings &&
    route.lineStrings.length > 0
  );
});

// Get difficulty color for styling
const difficultyColor = computed(() => {
  switch (difficulty.value) {
    case 'Easy': return '#4CAF50';
    case 'Medium': return '#FF9800';
    case 'Difficult': return '#F44336';
    case 'Extra Difficult': return '#9C27B0';
    default: return '#999';
  }
});

// Title management
const isEditing = ref(false);
const titleElement = ref(null);
const displayTitle = computed(() => {
  const storeTitle = store.state.planTrail.title;
  if (storeTitle) {
    return storeTitle;
  }
  // Default title with today's date
  const today = new Date();
  const options = { year: 'numeric', month: 'long', day: 'numeric' };
  const dateStr = today.toLocaleDateString('en-US', options);
  return `Hike • ${dateStr}`;
});

// Create a mutable copy of routes for draggable
const mutableRoutes = ref([]);
const emptyDayModel = ref([]); // Model for empty days

// Watch for changes in routes and update mutableRoutes
watch(routes, (newRoutes) => {
  mutableRoutes.value = [...newRoutes];
}, { immediate: true });

// Group routes by day - reactive to allow dragging
const dayRoutes = reactive({});

// Day editing modal state
const dayEditModalOpen = ref(false);
const editingDay = ref(null);
const dayNames = reactive({});
const dayDescriptions = reactive({});
const dayOrder = ref([]);
const isDayDragging = ref(false);
const initialDayOrderSnapshot = ref([]);

// Auto-scroll state
const autoScrollInterval = ref(null);
const scrollContainer = ref(null);
const isDragging = ref(false);

// Load day info from store on mount
onMounted(() => {
  if (store.state.planTrail.dayInfo) {
    Object.entries(store.state.planTrail.dayInfo).forEach(([dayNumber, info]) => {
      dayNames[dayNumber] = info.name || '';
      dayDescriptions[dayNumber] = info.description || '';
    });
  }
});

// Watch for dayInfo changes (e.g., when store initializes asynchronously from localStorage)
watch(
  () => store.state.planTrail.dayInfo,
  (newDayInfo) => {
    if (newDayInfo) {
      Object.entries(newDayInfo).forEach(([dayNumber, info]) => {
        dayNames[dayNumber] = info.name || '';
        dayDescriptions[dayNumber] = info.description || '';
      });
    }
  },
  { deep: true, immediate: true }
);

// Update dayRoutes when mutableRoutes changes
watchEffect(() => {
  // Clear existing
  Object.keys(dayRoutes).forEach(key => delete dayRoutes[key]);

  mutableRoutes.value.forEach(route => {
    // Only assign to day 1 if we have multiple routes and this route has no day
    const day = route.day || (shouldShowDays.value ? 1 : undefined);

    if (day !== undefined) {
      if (!dayRoutes[day]) {
        dayRoutes[day] = [];
      }
      dayRoutes[day].push(route);
    }
  });


});

watchEffect(() => {
  if (!shouldShowDays.value) {
    dayOrder.value = [];
    return;
  }

  if (isDayDragging.value) {
    return;
  }

  const nextOrder = displayDays.value.map((dayNumber) => ({
    dayNumber,
    isPlaceholder: !(dayRoutes[dayNumber] && dayRoutes[dayNumber].length > 0)
  }));

  dayOrder.value = nextOrder;
});

// Helper to get empty list for draggable binding
const emptyDayLists = reactive({});
function getEmptyDayList(day) {
  // Create a reactive list for each empty day
  if (!emptyDayLists[day]) {
    emptyDayLists[day] = [];
  }
  return emptyDayLists[day];
}

// Define emits for parent component communication
const emit = defineEmits([
  "import",
  "add-new",
  "edit",
  "delete",
  "collapse",
  "route-style-change",
  "replace"
]);

const headingId = `routes-heading-${Math.random().toString(36).slice(2)}`;

const menuOpen = ref(null);
const menuPosition = ref({ top: '0px', left: '0px', transform: 'translateX(-100%)' });
const dayMenuOpen = ref(null);
const isCollapsed = ref(false);
const styleDialogRoute = ref(null);
const routeStyleDialog = ref(null);
const difficultyDialog = ref(null);
const routeUsageModal = ref(null);
const editingRouteId = ref(null);
const originalRouteName = ref('');
const editingDayNumber = ref(null);
const originalDayName = ref('');

function positionMenuFromEvent(event) {
  if (event?.currentTarget) {
    const btn = event.currentTarget;
    const rect = btn.getBoundingClientRect();
    // backdrop-filter on .route-panel creates a new containing block for
    // position:fixed children, so we must offset by the panel's position.
    const panel = btn.closest('.route-panel');
    const panelRect = panel ? panel.getBoundingClientRect() : { top: 0, left: 0 };
    const estimatedMenuHeight = 300;
    const openUpward = rect.bottom + 8 + estimatedMenuHeight > window.innerHeight;
    menuPosition.value = {
      top: openUpward
        ? `${rect.top - panelRect.top - 8}px`
        : `${rect.bottom - panelRect.top + 8}px`,
      left: `${rect.right - panelRect.left}px`,
      transform: openUpward ? 'translateX(-100%) translateY(-100%)' : 'translateX(-100%)',
    };
  }
}

function openMenu(i, event) {
  if (event && typeof event.stopPropagation === 'function') {
    event.stopPropagation();
  }
  dayMenuOpen.value = null;

  if (menuOpen.value === i) {
    menuOpen.value = null;
    return;
  }

  positionMenuFromEvent(event);
  menuOpen.value = i;
}

function openDayMenu(dayNumber, event) {
  if (event && typeof event.stopPropagation === 'function') {
    event.stopPropagation();
  }
  menuOpen.value = null;

  if (dayMenuOpen.value === dayNumber) {
    dayMenuOpen.value = null;
    return;
  }

  positionMenuFromEvent(event);
  dayMenuOpen.value = dayNumber;
}

// Methods
function formatDistance(m, convertToBigUnitsAfter1000m = true) {
  const preference = MeasurementPrefUtility.preference()
  return preference.smallUnitFormatter(m, convertToBigUnitsAfter1000m)
}

function selectRoute(route) {
  if (route.id !== activeRouteId.value) {
    store.dispatch('planTrail/switchToRoute', route.id);
  }
}

function dayRouteSummary(dayNumber) {
  const count = (dayRoutes[dayNumber] && dayRoutes[dayNumber].length) || 0;
  return count === 1 ? '1 route' : `${count} routes`;
}

async function handleAddNew(evt) {
  store.commit('planTrail/createNewRoute');
  // Refresh the routes display to show the new route
  await store.dispatch('planTrail/addAllRoutesToMap');
  emit('add-new');
  // Return button to normal (remove focus/active state)
  try {
    const el = evt && evt.currentTarget;
    if (el && typeof el.blur === 'function') el.blur();
  } catch (_) {}
}

function handleEdit(route) {
  emit('edit', route);
  menuOpen.value = null;
}

function handleSetRouteStyle(route) {
  styleDialogRoute.value = route;
  menuOpen.value = null;

  // Show the modal using the exposed method
  nextTick(() => {
    if (routeStyleDialog.value) {
      routeStyleDialog.value.show();
    }
  });
}

function showUsageModal(route) {
  menuOpen.value = null;

  // Show the modal by calling the component's show method
  nextTick(() => {
    if (routeUsageModal.value) {
      routeUsageModal.value.show(route);
    }
  });
}

async function updateRouteStyle(newStyle) {
  if (styleDialogRoute.value) {
    emit('route-style-change', {
      routeId: styleDialogRoute.value.id,
      style: newStyle
    });

    store.commit('planTrail/updateRouteStyle', {
      routeId: styleDialogRoute.value.id,
      style: newStyle
    });
    // Refresh the routes display to update the map with new styling
    await store.dispatch('planTrail/addAllRoutesToMap');
  }
}

function handleDelete(route) {
  if (routes.value.length <= 1) {
    menuOpen.value = null;
    return;
  }

  emit('delete', route);
  menuOpen.value = null;
}

function handleReplace(route) {
  emit('replace', route);
  menuOpen.value = null;
}

function handleImport(evt) {
  emit('import');
  // Remove focus/active state after click
  try {
    const el = evt && evt.currentTarget;
    if (el && typeof el.blur === 'function') el.blur();
  } catch (_) {}
}

// Build a GeoJSON FeatureCollection from route lineStrings
function buildFeatureCollectionFromRoutes(routeFilter) {
  const routesArray = store.getters['planTrail/routesArray'];
  const filteredRoutes = routeFilter ? routesArray.filter(routeFilter) : routesArray;

  const features = [];
  filteredRoutes.forEach(route => {
    if (!route.lineStrings || route.lineStrings.length === 0) return;

    // Collect all coordinates from this route's lineStrings into one LineString
    let allCoords = [];
    route.lineStrings.forEach(ls => {
      const lineStringFeature = plainToClass(LineStringFeature, ls);
      const coords = lineStringFeature.lineStringGeometry.coordinates;
      if (coords && coords.length > 0) {
        // Avoid duplicating the join point between consecutive lineStrings
        if (allCoords.length > 0) {
          allCoords = allCoords.concat(coords.slice(1));
        } else {
          allCoords = allCoords.concat(coords);
        }
      }
    });

    if (allCoords.length >= 2) {
      features.push({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: allCoords
        },
        properties: { name: route.name, routeStyle: route.route_style }
      });
    }
  });

  return { type: "FeatureCollection", features };
}

function launchFlyoverForAllRoutes() {
  const fc = buildFeatureCollectionFromRoutes(route => route.route_style !== 'transport');
  if (fc.features.length === 0) return;

  if (typeof window.showMapModalForCoordinates === 'function') {
    window.showMapModalForCoordinates(fc);
  }
}

async function launchFlyoverForRoute(route) {
  if (!window.user) {
    window.onBoardingVue?.launchFor(OnBoardingGoal.CreateAccount);
    return;
  }

  menuOpen.value = null;
  const routesArray = store.getters['planTrail/routesArray'];
  const fullRoute = routesArray.find(r => r.id === route.id);
  if (!fullRoute) return;

  const fc = buildFeatureCollectionFromRoutes(r => r.id === route.id);
  if (fc.features.length === 0) return;

  const ready = await ensureFlyoverMounted();
  if (ready) {
    window.showMapModalForCoordinates(fc);
  }
}

function toggleCollapse() {
  isCollapsed.value = !isCollapsed.value;
  emit('collapse');
}

// Merge helpers
function isFirstRoute(route) {
  const idx = getRouteGlobalIndex(route);
  return idx === 0;
}

function isLastRoute(route) {
  const idx = getRouteGlobalIndex(route);
  return idx === (mutableRoutes.value.length - 1);
}

async function mergeWithPrevious(route) {
  try {
    await store.dispatch('planTrail/mergeRouteWithPrevious', route.id);
  } finally {
    menuOpen.value = null;
  }
}

async function mergeWithNext(route) {
  try {
    await store.dispatch('planTrail/mergeRouteWithNext', route.id);
  } finally {
    menuOpen.value = null;
  }
}

// Handle drag end event
function onDragEnd() {
  // Stop auto-scrolling
  isDragging.value = false;
  stopAutoScroll();

  // Get the new order of route IDs
  const reorderedRouteIds = mutableRoutes.value.map(route => route.id);

  // Update the positions in the store
  store.commit('planTrail/updateRoutePositions', reorderedRouteIds);
}

// Handle drag change event
function onDragChange(evt, currentDay) {
  

  // Handle different event types
  if (evt.added) {
    
    // A route was added to this day's list
    const addedRoute = evt.added.element;
    if (!addedRoute.day || addedRoute.day !== currentDay) {
      // Update the route's day assignment
      addedRoute.day = currentDay;
    }
  } else if (evt.removed) {
    
    // A route was removed from this day's list
    // The route's day will be updated when it's added to another list
  } else if (evt.moved) {
    
    // Route moved within the same day - no day change needed
  }

  // Wait for Vue to update the reactive state
  nextTick(() => {
    // Build the new route structure based on current state
    const newRoutesByDay = {};
    const totalRouteCount = routes.value.length;

    // Go through all days and collect their routes
    displayDays.value.forEach(day => {
      const routes = [];

      // Check regular day routes
      if (dayRoutes[day] && dayRoutes[day].length > 0) {
        routes.push(...dayRoutes[day].map(r => r.id));
      }

      // Check empty day lists (for routes dragged to empty days)
      if (emptyDayLists[day] && emptyDayLists[day].length > 0) {
        routes.push(...emptyDayLists[day].map(r => r.id));
        // Move routes from empty list to regular list
        dayRoutes[day] = [...(dayRoutes[day] || []), ...emptyDayLists[day]];
        emptyDayLists[day] = [];
      }

      if (routes.length > 0) {
        newRoutesByDay[day] = routes;
      }
    });

    const assignedRouteCount = Object.values(newRoutesByDay).reduce((sum, ids) => sum + ids.length, 0);

    if (assignedRouteCount !== totalRouteCount) {
      // The dragged route has not been reassigned yet; wait for the paired change event
      return;
    }

    const orderedRoutesByDay = {};
    Object.keys(newRoutesByDay)
      .map(d => parseInt(d, 10))
      .sort((a, b) => a - b)
      .forEach(dayNumber => {
        orderedRoutesByDay[dayNumber] = newRoutesByDay[dayNumber];
      });

    // Validate the drag operation
    const isValid = validateDragOperation(orderedRoutesByDay);

    if (!isValid) {
      // Force refresh from store
      mutableRoutes.value = [...routes.value];
      return;
    }

    // Update store with the new day assignments
    store.commit('planTrail/updateRouteDaysAndPositions', {
      routesByDay: orderedRoutesByDay,
      noDay: []
    });
  });
}

function onRouteDragStart() {
  isDragging.value = true;
}

function onRouteDragEnd() {
  isDragging.value = false;
  stopAutoScroll();
}

function onDayDragStart() {
  isDayDragging.value = true;
  isDragging.value = true;
  initialDayOrderSnapshot.value = dayOrder.value
    .filter(item => !item.isPlaceholder && dayRoutes[item.dayNumber] && dayRoutes[item.dayNumber].length > 0)
    .map(item => item.dayNumber);
}

function onDayMove(evt) {
  // Trigger auto-scroll check when moving days
  if (evt.originalEvent) {
    handleDragOver(evt.originalEvent);
  }
  return true; // Allow the move
}

function onRouteMove(evt) {
  // Trigger auto-scroll check when moving routes
  if (evt.originalEvent) {
    handleDragOver(evt.originalEvent);
  }
  return true; // Allow the move
}

function onDayDragEnd() {
  // Stop auto-scrolling
  isDragging.value = false;
  stopAutoScroll();

  const activeDayItems = dayOrder.value
    .filter(item => !item.isPlaceholder && dayRoutes[item.dayNumber] && dayRoutes[item.dayNumber].length > 0);

  const newOrder = activeDayItems.map(item => item.dayNumber);
  const previousOrder = initialDayOrderSnapshot.value;

  const orderChanged =
    previousOrder.length !== newOrder.length ||
    previousOrder.some((day, index) => day !== newOrder[index]);

  if (!orderChanged || newOrder.length === 0) {
    initialDayOrderSnapshot.value = [];
    isDayDragging.value = false;
    return;
  }

  const routesByDay = {};
  const newDayInfo = {};
  const newDayNames = {};
  const newDayDescriptions = {};

  let newDayNumber = 1;

  newOrder.forEach(originalDayNumber => {
    const routesForDay = dayRoutes[originalDayNumber] || [];
    if (routesForDay.length === 0) {
      return;
    }

    routesByDay[newDayNumber] = routesForDay.map(route => route.id);

    const name = dayNames[originalDayNumber] || '';
    const description = dayDescriptions[originalDayNumber] || '';

    newDayNames[newDayNumber] = name;
    newDayDescriptions[newDayNumber] = description;

    if (name || description) {
      newDayInfo[newDayNumber] = { name, description };
    }

    newDayNumber++;
  });

  store.commit('planTrail/updateRouteDaysAndPositions', {
    routesByDay,
    noDay: []
  });

  store.commit('planTrail/setDayInfo', newDayInfo);

  Object.keys(dayNames).forEach(key => delete dayNames[key]);
  Object.keys(dayDescriptions).forEach(key => delete dayDescriptions[key]);

  Object.entries(newDayNames).forEach(([day, name]) => {
    dayNames[Number(day)] = name;
  });

  Object.entries(newDayDescriptions).forEach(([day, description]) => {
    dayDescriptions[Number(day)] = description;
  });

  initialDayOrderSnapshot.value = [];
  isDayDragging.value = false;
}

// Legacy function - kept for compatibility but not used
function onDragEndWithDay(evt) {
  // Now handled by onDragChange
}

// Validate drag operation
function validateDragOperation(routesByDay) {
  const hasInvalidDay = Object.keys(routesByDay).some((dayKey) => {
    const dayNumber = Number(dayKey);
    return Number.isNaN(dayNumber) || dayNumber < 1;
  });

  if (hasInvalidDay) {
    return false;
  }

  const seenRouteIds = new Set();
  for (const routeIds of Object.values(routesByDay)) {
    routeIds.forEach((routeId) => {
      seenRouteIds.add(routeId);
    });
  }

  return seenRouteIds.size === routes.value.length;
}

// Check move function for draggable
function checkMove(evt) {
  // For now, allow all moves - we'll validate after the drop
  return true;
}

// Update routes for a specific day
function updateDayRoutes(day, newRoutes) {
  // This is called during dragging, we don't need to do anything here
  // The actual update happens in onDragEndWithDay
}

// Get global index for a route
function getRouteGlobalIndex(route) {
  return mutableRoutes.value.findIndex(r => r.id === route.id);
}

// Edit day name
function editDay(day) {
  editingDay.value = day;
  dayEditModalOpen.value = true;
}

// Save day information
function saveDayInfo(dayInfo) {
  dayNames[dayInfo.dayNumber] = dayInfo.name;
  dayDescriptions[dayInfo.dayNumber] = dayInfo.description;

  // Update store with day information
  store.dispatch('planTrail/updateDayInfo', {
    dayNumber: dayInfo.dayNumber,
    name: dayInfo.name,
    description: dayInfo.description
  });
}

// Close menu when clicking outside
function handleClickOutside(event) {
  // Check if the clicked element is not part of any menu
  const target = event.target instanceof HTMLElement ? event.target : null;
  if (target && !target.closest('.route-card__actions')) {
    menuOpen.value = null;
  }
  if (target && !target.closest('.day-card__actions')) {
    dayMenuOpen.value = null;
  }
}

// Title editing functions
function startEditing() {
  isEditing.value = true;
  nextTick(() => {
    if (titleElement.value) {
      titleElement.value.focus();
      // Select all text
      const range = document.createRange();
      range.selectNodeContents(titleElement.value);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  });
}

function saveTitle() {
  isEditing.value = false;
  if (titleElement.value) {
    const newTitle = titleElement.value.textContent.trim();
    if (newTitle && newTitle !== displayTitle.value) {
      store.commit('planTrail/setTitle', newTitle);
    } else if (!newTitle) {
      // Reset to default if empty
      titleElement.value.textContent = displayTitle.value;
    }
  }
}

function handleEnterKey() {
  if (titleElement.value) {
    titleElement.value.blur();
  }
}

// Route name editing functions
function startEditingRouteName(route) {
  // Don't start editing if we're already editing another route
  if (editingRouteId.value && editingRouteId.value !== route.id) {
    return;
  }

  editingRouteId.value = route.id;
  originalRouteName.value = route.name;

  nextTick(() => {
    // Use a data attribute selector instead of ref
    const routeNameEl = document.querySelector(`[data-route-id="${route.id}"]`);
    if (routeNameEl) {
      routeNameEl.focus();
      // Select all text
      const range = document.createRange();
      range.selectNodeContents(routeNameEl);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  });
}

function saveRouteName(route) {
  if (editingRouteId.value !== route.id) return;

  const routeNameEl = document.querySelector(`[data-route-id="${route.id}"]`);
  if (routeNameEl) {
    const newName = routeNameEl.textContent.trim();
    if (newName && newName !== route.name) {
      store.commit('planTrail/updateRouteName', {
        routeId: route.id,
        name: newName
      });
    } else if (!newName) {
      // Reset to original if empty
      routeNameEl.textContent = originalRouteName.value;
    }
  }
  editingRouteId.value = null;
}

function handleRouteEnterKey(route) {
  const routeNameEl = document.querySelector(`[data-route-id="${route.id}"]`);
  if (routeNameEl) {
    routeNameEl.blur();
  }
}

function cancelRouteEdit(route) {
  const routeNameEl = document.querySelector(`[data-route-id="${route.id}"]`);
  if (routeNameEl) {
    routeNameEl.textContent = originalRouteName.value;
    routeNameEl.blur();
  }
  editingRouteId.value = null;
}

// Day name editing functions
function startEditingDayName(dayItem) {
  // Don't start editing if we're already editing another day
  if (editingDayNumber.value && editingDayNumber.value !== dayItem.dayNumber) {
    return;
  }

  editingDayNumber.value = dayItem.dayNumber;
  originalDayName.value = dayNames[dayItem.dayNumber] || '';

  nextTick(() => {
    // Use a data attribute selector instead of ref
    const dayNameEl = document.querySelector(`[data-day-number="${dayItem.dayNumber}"]`);
    if (dayNameEl) {
      dayNameEl.focus();
      // Select all text
      const range = document.createRange();
      range.selectNodeContents(dayNameEl);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  });
}

function saveDayName(dayItem) {
  if (editingDayNumber.value !== dayItem.dayNumber) return;

  const dayNameEl = document.querySelector(`[data-day-number="${dayItem.dayNumber}"]`);
  if (dayNameEl) {
    const newName = dayNameEl.textContent.trim();
    const currentName = dayNames[dayItem.dayNumber] || '';

    if (newName !== currentName) {
      dayNames[dayItem.dayNumber] = newName;

      // Update store with day information
      store.dispatch('planTrail/updateDayInfo', {
        dayNumber: dayItem.dayNumber,
        name: newName,
        description: dayDescriptions[dayItem.dayNumber] || ''
      });
    } else if (!newName) {
      // Reset to original if empty
      dayNameEl.textContent = originalDayName.value;
    }
  }
  editingDayNumber.value = null;
}

function handleDayEnterKey(dayItem) {
  const dayNameEl = document.querySelector(`[data-day-number="${dayItem.dayNumber}"]`);
  if (dayNameEl) {
    dayNameEl.blur();
  }
}

function cancelDayEdit(dayItem) {
  const dayNameEl = document.querySelector(`[data-day-number="${dayItem.dayNumber}"]`);
  if (dayNameEl) {
    dayNameEl.textContent = originalDayName.value;
    dayNameEl.blur();
  }
  editingDayNumber.value = null;
}

function showDifficultyDialog() {
  nextTick(() => {
    if (difficultyDialog.value) {
      difficultyDialog.value.show();
    }
  });
}

// Auto-scroll functionality
function handleDragOver(event) {
  // Only handle auto-scroll during active drag operations
  if (!isDragging.value) {
    stopAutoScroll();
    return;
  }

  // Get the scrollable container
  const container = document.querySelector('.route-list-container');
  if (!container) return;

  scrollContainer.value = container;

  const containerRect = container.getBoundingClientRect();
  const mouseY = event.clientY;

  // Define scroll zones (50px from top and bottom of container)
  const scrollZoneSize = 100;
  const scrollSpeed = 5;

  const distanceFromTop = mouseY - containerRect.top;
  const distanceFromBottom = containerRect.bottom - mouseY;

  // Check if mouse is within the container bounds vertically
  const isWithinContainer = mouseY >= containerRect.top && mouseY <= containerRect.bottom;

  if (!isWithinContainer) {
    stopAutoScroll();
    return;
  }

  // Check if we're in a scroll zone
  if (distanceFromTop < scrollZoneSize && distanceFromTop > 0) {
    // Scroll up
    startAutoScroll(-scrollSpeed);
  } else if (distanceFromBottom < scrollZoneSize && distanceFromBottom > 0) {
    // Scroll down
    startAutoScroll(scrollSpeed);
  } else {
    // Not in scroll zone, stop scrolling
    stopAutoScroll();
  }
}

function startAutoScroll(speed) {
  // Don't start a new interval if one is already running
  if (autoScrollInterval.value) {
    return;
  }

  autoScrollInterval.value = setInterval(() => {
    if (scrollContainer.value) {
      scrollContainer.value.scrollTop += speed;
    }
  }, 16); // ~60fps
}

function stopAutoScroll() {
  if (autoScrollInterval.value) {
    clearInterval(autoScrollInterval.value);
    autoScrollInterval.value = null;
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('dragover', handleDragOver);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('dragover', handleDragOver);
  stopAutoScroll();
});

function handleDayEdit(dayNumber) {
  dayMenuOpen.value = null;
  editDay(dayNumber);
}

async function addRouteToDay(dayNumber) {
  dayMenuOpen.value = null;
  store.commit('planTrail/createNewRoute');
  const newRouteId = store.state.planTrail.activeRouteKey;
  if (newRouteId) {
    store.commit('planTrail/updateRouteDay', { routeId: newRouteId, day: dayNumber });
    await store.dispatch('planTrail/addAllRoutesToMap');
    emit('add-new', dayNumber);
  }
}

function hasDayInfo(dayNumber) {
  const name = dayNames[dayNumber];
  const description = dayDescriptions[dayNumber];
  return (name && name.trim().length > 0) || (description && description.trim().length > 0);
}

// Check if a day can be deleted (must have routes or be non-empty)
function canDeleteDay(dayNumber) {
  // Can only delete days that have routes or custom info
  const hasRoutes = dayRoutes[dayNumber] && dayRoutes[dayNumber].length > 0;
  const hasInfo = hasDayInfo(dayNumber);
  return hasRoutes || hasInfo;
}

// Delete a day and all routes within it, with confirmation
async function deleteDay(dayNumber) {
  dayMenuOpen.value = null;

  // Capture route IDs BEFORE any mutations
  const routesInDay = dayRoutes[dayNumber] || [];
  const routeIdsToDelete = routesInDay.map(route => route.id);
  const routeCount = routeIdsToDelete.length;
  const dayName = dayNames[dayNumber] || `Day ${dayNumber}`;

  // Ask for confirmation
  const confirmMessage = routeCount > 0
    ? `Are you sure you want to delete ${dayName} and all ${routeCount} route${routeCount > 1 ? 's' : ''} within it? This cannot be undone.`
    : `Are you sure you want to delete ${dayName}? This cannot be undone.`;

  if (!confirm(confirmMessage)) {
    return;
  }

  // Build snapshot of all current days and their routes BEFORE any changes
  const currentDaysSnapshot = {};
  Object.keys(dayRoutes).forEach(day => {
    const dayNum = parseInt(day);
    if (dayRoutes[dayNum] && dayRoutes[dayNum].length > 0) {
      currentDaysSnapshot[dayNum] = dayRoutes[dayNum].map(route => route.id);
    }
  });

  // Get remaining days (excluding the deleted day)
  const remainingDays = Object.keys(currentDaysSnapshot)
    .map(d => parseInt(d))
    .filter(d => d !== dayNumber)
    .sort((a, b) => a - b);

  if (remainingDays.length === 0) {
    // If no days remain after deletion, delete all routes and clear everything
    if (routeIdsToDelete.length > 0) {
      routeIdsToDelete.forEach(routeId => {
        store.commit('planTrail/removeRoute', routeId);
      });
    }

    store.commit('planTrail/updateRouteDaysAndPositions', {
      routesByDay: {},
      noDay: []
    });
    store.commit('planTrail/setDayInfo', {});

    Object.keys(dayNames).forEach(key => delete dayNames[key]);
    Object.keys(dayDescriptions).forEach(key => delete dayDescriptions[key]);

    // Refresh the map
    await store.dispatch('planTrail/addAllRoutesToMap');
    return;
  }

  // Build new route structure with renumbered days
  // Exclude the deleted day entirely
  const newRoutesByDay = {};
  const newDayInfo = {};
  const newDayNames = {};
  const newDayDescriptions = {};

  let newDayNumber = 1;

  remainingDays.forEach(originalDayNumber => {
    // Get route IDs from snapshot - these are the ones we're KEEPING
    const routeIdsForDay = currentDaysSnapshot[originalDayNumber] || [];

    if (routeIdsForDay.length > 0) {
      newRoutesByDay[newDayNumber] = routeIdsForDay;
    }

    // Copy day info if it exists
    const name = dayNames[originalDayNumber] || '';
    const description = dayDescriptions[originalDayNumber] || '';

    newDayNames[newDayNumber] = name;
    newDayDescriptions[newDayNumber] = description;

    if (name || description) {
      newDayInfo[newDayNumber] = { name, description };
    }

    newDayNumber++;
  });

  // Update store with new structure (this will handle day reassignments)
  store.commit('planTrail/updateRouteDaysAndPositions', {
    routesByDay: newRoutesByDay,
    noDay: []
  });

  // NOW delete the routes that were in the deleted day
  // Do this AFTER updating positions so they don't get reassigned
  if (routeIdsToDelete.length > 0) {
    routeIdsToDelete.forEach(routeId => {
      store.commit('planTrail/removeRoute', routeId);
    });
  }

  store.commit('planTrail/setDayInfo', newDayInfo);

  // Update local reactive objects
  Object.keys(dayNames).forEach(key => delete dayNames[key]);
  Object.keys(dayDescriptions).forEach(key => delete dayDescriptions[key]);

  Object.entries(newDayNames).forEach(([day, name]) => {
    dayNames[Number(day)] = name;
  });

  Object.entries(newDayDescriptions).forEach(([day, description]) => {
    dayDescriptions[Number(day)] = description;
  });

  // Refresh the map to remove deleted routes
  await store.dispatch('planTrail/addAllRoutesToMap');
}
</script>

<style scoped>
/* panel container */
.route-panel {
  position: absolute;
  top: 1rem;
  left: 1rem;
  height: 85vh;
  width: 320px;
  background: rgba(18, 18, 22, 0.95);
  color: #fff;
  border-radius: 18px;
  box-shadow: 0 24px 48px rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
  overflow: visible; /* Allow dropdowns to overflow */
  z-index: 500; /* above Mapbox controls */
  transition: transform 0.3s ease, opacity 0.3s ease;
  backdrop-filter: blur(16px);
}

.route-panel.condensed {
  height: auto;
  min-height: 200px;
  padding-bottom: 1rem;
}

/* header */
.panel-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.panel-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: text;
  outline: none;
  min-width: 100px;
  transition: background-color 0.2s;
}

.panel-title:hover {
  background-color: rgba(255, 255, 255, 0.05);
  border-radius: 4px;
  padding: 2px 4px;
  margin: -2px -4px;
}

.panel-title:focus {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 2px 4px;
  margin: -2px -4px;
}

.icon-btn {
  background: transparent;
  border: none;
  color: inherit;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 0;
}

.icon-btn:hover {
  color: var(--primary);
}

/* actions */
.panel-actions {
  display: flex;
  gap: 0.75rem;
  padding: 0.75rem 1rem 1rem;
}

.panel-action-btn {
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  border: none;
  border-radius: 999px;
  background: #fddf00;
  color: #111;
  font-weight: 600;
  letter-spacing: 0.2px;
  padding: 0.65rem 0.9rem;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.panel-action-btn:hover {
  transform: translateY(-1px);
}

.panel-action-btn .material-icons {
  font-size: 18px;
}

/* list container */
.route-list-container {
  flex: 1 1 auto;
  overflow-y: auto;
  overflow-x: visible;
  padding: 0 1rem 1.5rem;
}

.days-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.days-container {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.day-card {
  background: #1f2026;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.45);
  transition: opacity 0.2s ease, transform 0.2s ease, border 0.2s ease;
}

.day-card--inactive {
  opacity: 0.55;
}

.day-card--dragging {
  transform: scale(0.98);
}

.day-card--ghost {
  opacity: 0.6;
  transform: translateY(4px);
}

.day-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
}

.day-card__title {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.day-card__title-text {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.day-card__name {
  font-size: 0.95rem;
  font-weight: 600;
  line-height: 1.2;
  cursor: text;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.day-card__name:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.day-card__name[contenteditable="true"] {
  background-color: rgba(255, 255, 255, 0.12);
}

.day-card__meta {
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: rgba(255, 255, 255, 0.55);
}

.day-card__actions {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.day-card__actions .icon-btn,
.route-card__actions .icon-btn {
  color: rgba(255, 255, 255, 0.7);
  padding: 0.3rem;
  border-radius: 50%;
  transition: background 0.2s ease, color 0.2s ease;
}

.day-card__actions .icon-btn:hover,
.route-card__actions .icon-btn:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.08);
}

.route-card__actions {
  position: relative;
  display: flex;
  align-items: center;
  margin-left: auto;
}

.day-card__description {
  margin: 0;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.68);
  line-height: 1.4;
  padding-left: 2.1rem;
}

.day-menu {
  position: relative;
}

.day-drag-handle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: move;
  padding: 0.1rem;
  border-radius: 50%;
  transition: background 0.2s ease, color 0.2s ease;
}

.day-drag-handle .material-icons, .route-drag-handle .material-icons {
  font-size: 18px;
}

.day-drag-handle:hover {
  background: rgba(255, 214, 0, 0.2);
  color: #fddf00;
}

.day-edit-btn {
  color: rgba(255, 255, 255, 0.7);
}

.day-edit-btn:hover {
  color: #fff;
}

.day-card__empty {
  border: 1px dashed rgba(255, 255, 255, 0.2);
  border-radius: 9px;
  padding: 1rem;
  text-align: center;
  min-height: 68px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.02);
}

.day-card__empty .day-empty-text {
  font-style: italic;
}

.route-stack {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.route-card {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.85rem;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #24252d;
  box-shadow: 0 10px 24px rgba(0, 0, 0, 0.4);
  transition: transform 0.2s ease, border 0.2s ease, background 0.2s ease;
}

.route-card:hover {
  background: #2b2c35;
}

.route-card--ghost {
  opacity: 0.55;
  border-color: rgba(253, 223, 0, 0.35);
  background: rgba(253, 223, 0, 0.12);
}

.route-card--active {
  border-color: #fddf00;
  box-shadow: 0 0 0 1px rgba(253, 223, 0, 0.35), 0 16px 28px rgba(0, 0, 0, 0.5);
  background: #2f303a;
}

.route-card--active:hover {
  background: #343540;
}

.route-drag-handle {
  cursor: move;
  opacity: 0.6;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 100px;
  padding: 0em;
  transition: opacity 0.2s ease, background 0.2s ease;
}

.route-drag-handle:hover {
  opacity: 1;
  background: rgba(255, 214, 0, 0.2);
}

.route-info {
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
}

.route-name {
  font-size: 0.8rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.route-name:hover {
  background-color: rgba(255, 255, 255, 0.08);
}

.route-name[contenteditable="true"] {
  background-color: rgba(255, 255, 255, 0.12);
  white-space: normal;
  overflow: visible;
  text-overflow: clip;
  word-break: break-word;
  padding: 2px 4px;
  margin: -2px -4px;
}

.route-meta {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.65);
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.menu-dropdown {
  position: fixed;
  min-width: 180px;
  background: #2f303b;
  border: 1px solid #434343;
  border-radius: 8px;
  box-shadow: 0 16px 32px rgba(0, 0, 0, 0.45);
  list-style: none;
  padding: 0.35rem 0;
  z-index: 1000;
}

.menu-dropdown li {
  width: 100%;
}

.menu-dropdown button {
  width: 100%;
  background: transparent;
  border: none;
  color: #fff;
  text-align: left;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
}

.menu-dropdown button:hover {
  background: rgba(255, 255, 255, 0.08);
}

.menu-dropdown button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.menu-dropdown button:disabled:hover {
  background: transparent;
}

/* footer */
.panel-footer {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
}

.footer-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.25rem;
}

.footer-row:last-child {
  margin-bottom: 0;
}

.difficulty-button {
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  transition: opacity 0.2s;
}

.difficulty-button:hover {
  opacity: 0.8;
}

.difficulty-badge {
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}

.override-indicator {
  font-size: 0.75rem;
  opacity: 0.8;
  vertical-align: super;
}

.no-routes {
  padding: 1rem;
  text-align: center;
  opacity: 0.75;
  font-style: italic;
}

/* scrollbar styling (webkit) */
.route-list-container::-webkit-scrollbar {
  width: 6px;
}
.route-list-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.15);
  border-radius: 3px;
}
.route-list-container::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.25);
}

/* Collapsed state */
.route-panel.collapsed {
  transform: translateX(-100%);
  opacity: 0;
  pointer-events: none;
  transition: transform 0.3s ease, opacity 0.3s ease;
}

/* Expand button */
.expand-button {
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: #1b1b1d;
  color: #fff;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.5);
  cursor: pointer;
  z-index: 499;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 600;
  transition: background 0.2s;
}

.expand-button:hover {
  background: #2c2c2e;
}

.expand-button svg {
  flex-shrink: 0;
}

/* Route style label */
.route-style-label {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  font-size: 0.55rem;
  font-weight: 600;
  letter-spacing: 0.8px;
  padding: 0.2rem 0.55rem;
  border-radius: 999px;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid transparent;
  margin-bottom: 0.2rem;
  width: fit-content;
}

.route-style-label--route {
  color: #ff8e7a;
  border-color: rgba(255, 65, 54, 0.35);
  background: rgba(255, 65, 54, 0.08);
}

.route-style-label--spur {
  color: #f7a8ff;
  border-color: rgba(230, 0, 255, 0.35);
  background: rgba(230, 0, 255, 0.1);
}

.route-style-label--alt {
  color: #8ceaff;
  border-color: rgba(0, 207, 232, 0.35);
  background: rgba(0, 207, 232, 0.1);
}

.route-style-label--transport {
  color: #FFFFFF;
  border-color: rgba(255, 255, 255, 0.25);
  background-image: repeating-linear-gradient(
    90deg,
    transparent,
    transparent 3px,
    rgba(255, 255, 255, 0.1) 3px,
    rgba(255, 255, 255, 0.1) 6px
  );
}

/* Reference badge */
.route-reference-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
  padding: 2px 6px;
  border: 1px solid #5a5a5a;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  margin-right: 0.25rem;
}

.route-reference-badge .material-icons {
  font-size: 14px;
}
</style>
